import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ToastModule } from 'primeng/toast';
import { ToastService } from '../../../@core/services/toast.service';
import { ButtonModule } from 'primeng/button';
import { FileSelectEvent, FileUpload, FileUploadEvent, FileUploadModule } from 'primeng/fileupload';

import { TextCasePipe } from "../../pipes/textCase.pipe";
import { CaseType } from 'src/app/@shared/services/case-format.service';
import { FileIconComponent } from '../file-icon/file-icon.component';
import { ButtonComponent } from '../../../@core/components/ui/button/button.component';
import { FILE_TYPE } from 'src/app/@core/models/enums';

@Component({
  selector: 'app-file-drop',
  imports: [CommonModule, ToastModule, ButtonModule, FileUploadModule, TextCasePipe, FileIconComponent, ButtonComponent],
  standalone: true,
  templateUrl: './file-drop.component.html',
  styleUrls: ['./file-drop.component.scss']
})
export class FileDropComponent implements OnInit, OnChanges {
  @Input() title: string = '';
  @Input() type: string = 'file';
  @Input() showDesc: boolean = true;
  @Input() fileTypes: string[] = [];
  @Input() maxFileSize: number = 1 * 1000 * 1000
  @Input() maxTotalFileSize?: number;
  @Input() multiple: boolean = false;
  @Input() maxFiles?: number;
  @Input() minimalView: boolean = false;
  @Input() existingFiles: File[] = [];
  @Output() uploadData = new EventEmitter<File | File[]>();
  @Output() isFileadded = new EventEmitter<boolean>();
  @Output() removeFile = new EventEmitter<File>();

  CASE_TYPE = CaseType;

  dragAreaClass: string = '';
  message: string = '';
  currentFiles: File[] = [];
  constructor(
    private toast: ToastService
  ) { }
  ngOnInit() {
    this.dragAreaClass = 'dragarea';
    this.initializeFiles();
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes['existingFiles'] && !changes['existingFiles'].firstChange) {
      this.syncWithExistingFiles();
    }
  }
  private syncWithExistingFiles() {
    // Compare files by name and size to avoid unnecessary updates
    const existingFileKeys = this.existingFiles.map(f => `${f.name}-${f.size}-${f.lastModified}`).sort();
    const currentFileKeys = this.currentFiles.map(f => `${f.name}-${f.size}-${f.lastModified}`).sort();

    // Only sync if the files are actually different
    if (JSON.stringify(existingFileKeys) !== JSON.stringify(currentFileKeys)) {
      this.currentFiles = [...this.existingFiles];
    }
  }
  private initializeFiles() {
    this.currentFiles = [...this.existingFiles];
  }
  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent) {
    this.dragAreaClass = 'droparea';
    event.preventDefault();
  }
  @HostListener('dragenter', ['$event'])
  onDragEnter(event: DragEvent) {
    this.dragAreaClass = 'droparea';
    event.preventDefault();
  }
  @HostListener('dragend', ['$event'])
  onDragEnd(event: DragEvent) {
    this.dragAreaClass = 'dragarea';
    event.preventDefault();
  }
  @HostListener('dragleave', ['$event'])
  onDragLeave(event: DragEvent) {
    this.dragAreaClass = 'dragarea';
    event.preventDefault();
  }
  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent) {
    this.dragAreaClass = 'dragarea';
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer?.files) {
      const files = Array.from(event.dataTransfer.files);
      this.saveFiles(files);
    }
  }
  onFileChange(event: FileUploadEvent) {
    if (event.files) {
      const files = event.files;
      this.saveFiles(files);
    }
  }
  onFileSelect(event: FileSelectEvent) {
    if (event.files) {
      const files = Array.from(event.files);
      this.saveFiles(files);
    }
  }
  choose(event: any, callback: any) {
    callback();
  }
  saveFiles(files: File[]) {
    if (!files || files.length === 0) return;
    // Handle uploads
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];
    for (const file of Array.from(files)) {
      const fileExtension = file.name.split('.').pop()?.toLowerCase() ?? '';
      if (this.maxTotalFileSize !== undefined && this.maxTotalFileSize > 0) {
        const totalSizeToAdd = files.reduce((sum, file) => sum + (file.size || 0), 0);
        const currentTotalSize = this.currentFiles.reduce((acc, file) => acc + file.size, 0);
        if (currentTotalSize + totalSizeToAdd > this.maxTotalFileSize) {
          this.toast.error(`The total uploaded file size exceeds the limit of ${this.maxTotalFileSize / (1000 * 1000)} MB`);
          return;
        }
      }
      const fileExists = this.currentFiles.some(existingFile =>
        existingFile.name === file.name && existingFile.size === file.size
      );
      if (fileExists) {
        this.toast.info(`File "${file.name}" is already selected`);
        continue;
      }
      if (
        this.fileTypes.length > 0 &&
        !this.fileTypes.includes('.' + fileExtension)
      ) {
        invalidFiles.push(file.name);
      } else if (file.size > this.maxFileSize) {
        invalidFiles.push(file.name);
      } else {
        validFiles.push(file);
      }
    }
    if (validFiles.length > 0) {
      let toAdd = validFiles;
      if (this.maxFiles !== undefined && this.maxFiles > 0) {
        const allowed = this.maxFiles - this.currentFiles.length;
        if (allowed <= 0) {
          this.toast.error(`Maximum ${this.maxFiles} file(s) allowed.`);
          return;
        }
        if (validFiles.length > allowed) {
          toAdd = validFiles.slice(0, allowed);
          this.toast.error(`Maximum ${this.maxFiles} file(s) allowed. Only ${allowed} file(s) were added.`);
        }
      }
      if (this.multiple) {
        // Multiple: append to existing files
        this.currentFiles = [...this.currentFiles, ...toAdd];
      } else {
        // Single: replace existing file
        this.currentFiles = toAdd;
      }
      this.uploadData.emit(this.currentFiles);
      this.isFileadded.emit(true);
    }
    if (invalidFiles.length > 0) {
      this.toast.error(
        `The following files are invalid or exceeds size limit: ${invalidFiles.join(
          ', '
        )}`
      );
      return;
    }
  }
  @ViewChild('fileUpload') fileUpload!: FileUpload;
  clearFiles() {
    if (this.fileUpload) {
      this.fileUpload.clear();
    }
    this.currentFiles = [];
    this.message = '';
  }

  resetToExistingFiles(): void {
    if (this.fileUpload) {
      this.fileUpload.clear();
    }
    this.currentFiles = [...this.existingFiles];
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1000;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  onRemoveFile(file: File) {
    this.removeFile.emit(file);
  }

  getFileType(file: File): FILE_TYPE | undefined {
    const extension = file.name.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return FILE_TYPE.PDF;
      case 'doc':
      case 'docx':
        return FILE_TYPE.WORD;
      case 'txt':
        return FILE_TYPE.TEXT;
      case 'html':
      case 'htm':
        return FILE_TYPE.HTML;
      case 'py':
        return FILE_TYPE.PYTHON;
      case 'md':
        return FILE_TYPE.MARKDOWN;
      case 'ppt':
      case 'pptx':
        return FILE_TYPE.POWERPOINT;
      case 'xls':
      case 'xlsx':
        return FILE_TYPE.EXCEL;
      case 'json':
        return FILE_TYPE.JSON;
      default:
        return undefined;
    }
  }
}
